import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import {
  DeleteButton,
  EditButton,
  PurpleButton,
} from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  InterfaceRequestResponse,
  deleteInterfaceRequest,
  getInterfaceRequestsByProjectId,
} from '@/services/InterfaceRequestService';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { InterfaceRequestStatus } from '@/enums/InterfaceRequestStatus';
import { InterfaceType } from '@/enums/InterfaceType';

const userTimeZone = moment.tz.guess();

function InterfaceRequests() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('InterfaceRequests.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('InterfaceRequests.editButton')}
        </EditButton>
        {/* <PurpleButton
          type="button"
          onClick={() => {
            navigate(`/gestion-project-ao/${value}/details`);
          }}
        >
          {t('InterfaceRequests.detailsButton')}
        </PurpleButton> */}
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteInterfaceRequest.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('InterfaceRequests.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteInterfaceRequest}
          />
        ),
      },
      {
        Header: t('InterfaceRequests.dueDateColumn'),
        accessor: 'dueDate',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = moment(value).format('YYYY-MM-DD');
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('InterfaceRequests.inputDataRequesterCompanyColumn'),
        accessor: 'inputDataRequesterCompany',
      },
      {
        Header: t('InterfaceRequests.inputDataRequesterCompanyContactColumn'),
        accessor: 'inputDataRequesterCompanyContact',
      },
      {
        Header: t('InterfaceRequests.interfaceDescriptionColumn'),
        accessor: 'interfaceDescription',
      },
      {
        Header: t('InterfaceRequests.detailsColumn'),
        accessor: 'details',
      },
      {
        Header: t('InterfaceRequests.interfaceTypeColumn'),
        accessor: 'interfaceType',
        Cell: ({ cell: { value } }) =>
          t(`InterfaceType.${InterfaceType[value]}`),
      },
      {
        Header: t('InterfaceRequests.dataProviderCompanyColumn'),
        accessor: 'dataProviderCompany',
      },
      {
        Header: t('InterfaceRequests.dataProviderContactCompanyColumn'),
        accessor: 'dataProviderContactCompany',
      },
      {
        Header: t('InterfaceRequests.dependenciesColumn'),
        accessor: 'dependencies',
      },
      {
        Header: t('InterfaceRequests.issuesAndRisksColumn'),
        accessor: 'issuesAndRisks',
      },
      {
        Header: t('InterfaceRequests.correctiveActionsColumn'),
        accessor: 'correctiveActions',
      },
      {
        Header: t('InterfaceRequests.commentsColumn'),
        accessor: 'comments',
      },
      {
        Header: t('InterfaceRequests.progressColumn'),
        accessor: 'progress',
        Cell: ({ cell: { value } }) => `${value}%`,
      },
      {
        Header: t('InterfaceRequests.statusColumn'),
        accessor: 'status',
        Cell: ({ cell: { value } }) =>
          t(`InterfaceRequestStatus.${InterfaceRequestStatus[value]}`),
      },

      {
        Header: t('InterfaceRequests.projectTitleColumn'),
        accessor: 'projectTitle',
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: interfaceRequests,
  } = useQuery<InterfaceRequestResponse | undefined>(
    ['interfaceRequests', pageIndex, pageSize, search],
    () =>
      getInterfaceRequestsByProjectId({
        projectId: id!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      enabled: id !== null,
    }
  );

  const handleDeleteInterfaceRequest = useMutation(
    async (id: string) => {
      await deleteInterfaceRequest(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'interfaceRequests',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((interfaceRequests?.count ?? 0) / pageSize);
  }, [pageSize, interfaceRequests]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('InterfaceRequests.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={interfaceRequests?.count}
            data={interfaceRequests?.interfaceRequests ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default InterfaceRequests;
